import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, formatDay, type Translations } from '../../i18n';
import { countLabel } from '../../i18n/labels';
import { TrendUpIcon, TrendDownIcon, TrendSteadyIcon } from '../shared/Icons';
import ShowMoreButton from '../shared/ShowMoreButton';
import { SectionTitle, StatTile } from './QualityTables';
import { ownTeamAgainstJury, ownTeamRounds, panelCompared, panelWith, rate } from '../../utils/officialStats';
import type { OfficialLevels, OfficialSeat, OfficialTimelineQuality, OfficialTimelineRow, OfficialTrend, OfficialTrendKey, OwnTeamCells, PanelCells } from '../../types';

/** The tournaments of one page of the list (#631, operator 2026-10-21). The
 *  trend and the chart read every tournament. */
const PAGE_SIZE = 10;

/** #620 — the statistics of an official over time: one row for each
 *  tournament, newest first. Every viewer sees the counts and the bouts of
 *  each level (#622). The server sends the quality numbers and `trend` only to
 *  canSeeAllOfficialStats, and the page shows what it received: the trend
 *  tiles, the chart and the quality columns. */
export default function OfficialTimeline({ timeline, trend }: { timeline: OfficialTimelineRow[]; trend?: Record<OfficialTrendKey, OfficialTrend> }) {
  const { t, lang } = useI18n();
  const [shown, setShown] = useState(PAGE_SIZE);
  if (timeline.length === 0) return null;
  const rows = timeline.slice(0, shown);
  const showQuality = !!trend;
  const percent = (n: number | null) => (n === null ? '—' : `${n}%`);
  const roundCount = (n: number) => countLabel(lang, n, { one: t.officials.roundsOne, few: t.officials.roundsFew, other: t.officials.roundsOther });

  const tiles: { key: OfficialTrendKey; label: string }[] = [
    { key: 'agreement', label: t.officials.agreement },
    { key: 'dropped', label: t.officials.dropped },
    { key: 'jury', label: t.officials.juryTotals },
  ];

  return (
    <div className="card p-4 space-y-4" data-testid="official-timeline">
      <SectionTitle title={t.officials.overTime} hint={showQuality ? t.officials.overTimeHint : t.officials.overTimeHintCounts} />

      {trend && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {tiles.map(({ key, label }) => {
            const tr = trend[key];
            const recent = rate(tr.recent.hits, tr.recent.total);
            const before = rate(tr.before.hits, tr.before.total);
            // The colour AND a word say "better" or "worse" (not the colour alone).
            const tone = tr.better === true ? 'text-green-600' : tr.better === false ? 'text-red-600' : 'text-gray-500';
            const verdict = tr.better === true ? t.officials.trendBetter : tr.better === false ? t.officials.trendWorse : null;
            const Icon = tr.direction === 'up' ? TrendUpIcon : tr.direction === 'down' ? TrendDownIcon : TrendSteadyIcon;
            const dirLabel = tr.direction === 'up' ? t.officials.trendUp : tr.direction === 'down' ? t.officials.trendDown : t.officials.trendSteady;
            return (
              <StatTile key={key} testId={`official-trend-${key}`} attrs={{ 'data-direction': tr.direction ?? 'none' }} title={label}>
                {tr.direction === null ? (
                  <div className="text-sm text-gray-400 mt-1">{t.officials.tooFewRounds}</div>
                ) : (
                  <>
                    <div className={`flex items-center gap-1.5 mt-1 font-black text-lg tabular-nums ${tone}`}>
                      <Icon className="w-5 h-5 shrink-0" /><span>{percent(recent)}</span>
                      <span className="text-xs font-normal" data-testid="official-trend-word">{dirLabel}{verdict && ` · ${verdict}`}</span>
                    </div>
                    <div className="text-xs text-gray-500 tabular-nums">
                      {t.officials.trendLast.replace('{pct}', percent(recent))} ({roundCount(tr.recent.total)})
                    </div>
                    <div className="text-xs text-gray-500 tabular-nums">
                      {t.officials.trendBefore.replace('{pct}', percent(before))} ({roundCount(tr.before.total)})
                    </div>
                  </>
                )}
              </StatTile>
            );
          })}
        </div>
      )}

      {trend && <AgreementChart timeline={timeline} />}
      {/* #644 — the own-team cards against the jury at each tournament. */}
      {trend && <OwnTeamChart timeline={timeline} />}

      {/* #631, F1 — below 1280 px each tournament is a block: the table of
          10 columns hid 7 of them on a phone. #644 added two columns, and the
          Lithuanian table needs about 1,110 px (measured 2026-10-22), so the
          table shows from 1280 px. Both forms are in the page; CSS shows one.
          The block form has its own test ids, so a count of the rows reads
          one form. */}
      <ul className="xl:hidden divide-y divide-gray-100 border-t border-gray-100 text-xs" data-testid="official-timeline-blocks">
        {rows.map(r => <TimelineBlock key={r.tournament_id} row={r} showQuality={showQuality} />)}
      </ul>

      <div className="hidden xl:block">
        <table className="w-full text-xs" data-testid="official-timeline-table">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-100">
              <th className="py-2 pr-3 font-medium whitespace-nowrap">{t.common.date}</th>
              <th className="py-2 pr-3 font-medium">{t.officials.colTournament}</th>
              <th className="py-2 px-2 font-medium text-center">{t.bout.ringReferee}</th>
              <th className="py-2 px-2 font-medium text-center">{t.bout.sideJudge}</th>
              <th className="py-2 px-2 font-medium text-center">{t.bout.jury}</th>
              <th className="py-2 px-2 font-medium text-center whitespace-nowrap">{t.officials.colLevels}</th>
              {showQuality && <>
                <th className="py-2 px-2 font-medium text-center">{t.officials.agreementShort}</th>
                <th className="py-2 px-2 font-medium text-center">{t.officials.dropped}</th>
                <th className="py-2 px-2 font-medium text-center">{t.officials.juryTotals}</th>
                <th className="py-2 px-2 font-medium text-center">{t.officials.stoppagesShort}</th>
                <th className="py-2 px-2 font-medium text-center">{t.officials.colPanel}</th>
                <th className="py-2 px-2 font-medium text-center">{t.officials.colOwnTeam}</th>
              </>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map(r => {
              const q = r.quality;
              return (
                <tr key={r.tournament_id} data-testid="official-timeline-row" data-tournament-id={r.tournament_id} className="align-top">
                  <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatDay(r.date, 'dd MMM yyyy')}</td>
                  <td className="py-2 pr-3 min-w-40"><Link to={`/tournaments/${r.tournament_id}`} className="hover:underline">{r.tournament_name}</Link></td>
                  <td className="py-2 px-2 text-center tabular-nums">{r.counts.ring_referee}</td>
                  <td className="py-2 px-2 text-center tabular-nums">{r.counts.side_judge}</td>
                  <td className="py-2 px-2 text-center tabular-nums">{r.counts.jury}</td>
                  {/* #622 — the counted bouts of each level in this tournament. */}
                  <td className="py-2 px-2 text-center tabular-nums whitespace-nowrap" data-testid="official-timeline-levels"
                    data-pro={r.levels.pro} data-high={r.levels.high} data-medium={r.levels.medium} data-low={r.levels.low}>
                    {r.levels.pro} · {r.levels.high} · {r.levels.medium} · {r.levels.low}
                  </td>
                  {showQuality && q && <>
                    <Cell testId="agreement" own={[q.agreement.matched, q.agreement.compared]} field={[q.field.agreement.matched, q.field.agreement.compared]} fieldLabel={t.officials.allJudges} />
                    <Cell testId="dropped" own={[q.dropped.dropped, q.dropped.rounds]} field={[q.field.dropped.dropped, q.field.dropped.rounds]} fieldLabel={t.officials.allJudges} />
                    <Cell testId="jury" own={[q.jury.overrides_differ, q.jury.approved_rounds]} field={[q.field.jury.overrides_differ, q.field.jury.approved_rounds]} fieldLabel={t.officials.allJuries} />
                    <td className="py-2 px-2 text-center tabular-nums">{q.stoppages.stoppages}</td>
                    <PanelCell panel={q.panel} />
                    <OwnTeamCell team={q.team} />
                  </>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ShowMoreButton shown={shown} total={timeline.length} onMore={() => setShown(shown + PAGE_SIZE)} testId="official-timeline-more" />
    </div>
  );
}

/** #644 — the cards of one tournament against the panel: the rate with its
 *  count, and the upheld dissents under it when there are any. */
function PanelCell({ panel }: { panel: PanelCells }) {
  const { t } = useI18n();
  const compared = panelCompared(panel);
  const withPanel = panelWith(panel);
  const mine = rate(withPanel, compared);
  return (
    <td className="py-2 px-2 text-center tabular-nums whitespace-nowrap" data-testid="official-timeline-panel"
      data-with={withPanel} data-compared={compared} data-upheld={panel.dissent_upheld}>
      {mine === null ? <span className="text-gray-400">—</span> : <span>{mine}% <span className="text-gray-400">({withPanel}/{compared})</span></span>}
      {panel.dissent_upheld > 0 && <div className="text-gray-400 whitespace-normal">{t.officials.cellUpheld.replace('{n}', String(panel.dissent_upheld))}</div>}
    </td>
  );
}

/** #644 — the own-team rounds of one tournament against the jury decision:
 *  the rate with its count, and the two directions under it. A card for the
 *  own corner and a card against it both count (operator, 2026-10-22). */
function OwnTeamCell({ team }: { team: OwnTeamCells }) {
  const { t } = useI18n();
  const rounds = ownTeamRounds(team);
  const off = ownTeamAgainstJury(team);
  return (
    <td className="py-2 px-2 text-center tabular-nums whitespace-nowrap" data-testid="official-timeline-own-team"
      data-against-jury={off} data-for={team.for_own} data-against={team.against_own} data-rounds={rounds}>
      {rounds === 0 ? <span className="text-gray-400">—</span> : <span>{rate(off, rounds)}% <span className="text-gray-400">({off}/{rounds})</span></span>}
      {off > 0 && <div className="text-gray-400 whitespace-normal">{ownSplit(t, team)}</div>}
    </td>
  );
}

/** #644 — the two directions of the own-team cards against the jury. */
function ownSplit(t: Translations, team: OwnTeamCells): string {
  return t.officials.cellOwnSplit.replace('{for}', String(team.for_own)).replace('{against}', String(team.against_own));
}

/** A quality number of one tournament: the percent with its count, and the
 *  field average of the tournament under it. */
function Cell({ testId, own, field, fieldLabel }: { testId: string; own: [number, number]; field: [number, number]; fieldLabel: string }) {
  const mine = rate(own[0], own[1]);
  const all = rate(field[0], field[1]);
  return (
    <td className="py-2 px-2 text-center tabular-nums whitespace-nowrap" data-testid={`official-timeline-${testId}`} data-own={mine ?? ''} data-field={all ?? ''}>
      {mine === null ? <span className="text-gray-400">—</span> : <span>{mine}% <span className="text-gray-400">({own[0]}/{own[1]})</span></span>}
      {/* #631, F2 — the field average only under a value of the official:
          under "—" it compared nothing. */}
      {mine !== null && all !== null && <div className="text-gray-400">{fieldLabel.replace('{pct}', `${all}%`)}</div>}
    </td>
  );
}

/** #631, F1 — one tournament as a block below 1280 px. */
function TimelineBlock({ row: r, showQuality }: { row: OfficialTimelineRow; showQuality: boolean }) {
  return (
    <QualityBlock title={r.tournament_name} href={`/tournaments/${r.tournament_id}`} date={r.date}
      counts={r.counts} levels={r.levels} quality={showQuality ? r.quality : undefined}
      testId="official-timeline-block" rowAttrs={{ 'data-tournament-id': r.tournament_id }} />
  );
}

/** #631, F1 — one block of numbers. The block shows the title (a link) and
 *  the date. Then it shows one line for each number that has data: the
 *  seats, the levels and the quality numbers (operator, 2026-10-21: each on a
 *  new line). The labels and the values are two aligned columns. The block
 *  leaves out a number with no data and does not show "—". #647 — one block
 *  serves a tournament of an official ("Over time") and an official of a
 *  tournament (the report of the Officials tab); `testId` names the block and
 *  prefixes the test ids of its lines. */
export function QualityBlock({ title, href, date, counts, levels, quality: q, testId, rowAttrs }: {
  title: string; href: string; date: string | null; counts: Record<OfficialSeat, number>; levels: OfficialLevels;
  quality: OfficialTimelineQuality | undefined; testId: string; rowAttrs: Record<string, string>;
}) {
  const { t } = useI18n();
  type Item = { key: string; label: string; own: [number, number]; field: [number, number]; fieldLabel: string };
  const candidates: Item[] = q ? [
    { key: 'agreement', label: t.officials.agreementShort, own: [q.agreement.matched, q.agreement.compared], field: [q.field.agreement.matched, q.field.agreement.compared], fieldLabel: t.officials.allJudges },
    { key: 'dropped', label: t.officials.dropped, own: [q.dropped.dropped, q.dropped.rounds], field: [q.field.dropped.dropped, q.field.dropped.rounds], fieldLabel: t.officials.allJudges },
    { key: 'jury', label: t.officials.juryTotals, own: [q.jury.overrides_differ, q.jury.approved_rounds], field: [q.field.jury.overrides_differ, q.field.jury.approved_rounds], fieldLabel: t.officials.allJuries },
  ] : [];
  const items = candidates.filter(i => i.own[1] > 0);
  const seats: [string, number][] = [
    [t.bout.ringReferee, counts.ring_referee], [t.bout.sideJudge, counts.side_judge], [t.bout.jury, counts.jury],
  ];
  return (
    <li data-testid={testId} {...rowAttrs} className="py-2.5 space-y-1">
      <div className="flex items-start justify-between gap-3">
        <Link to={href} className="font-medium text-sm min-w-0 break-words hover:underline">{title}</Link>
        {date && <span className="text-gray-500 tabular-nums whitespace-nowrap">{formatDay(date, 'dd MMM yyyy')}</span>}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        {seats.filter(([, n]) => n > 0).map(([label, n]) => (
          <div key={label} className="contents">
            <dt className="text-gray-500">{label}</dt>
            <dd className="font-semibold tabular-nums">{n}</dd>
          </div>
        ))}
        <dt className="text-gray-500">{t.officials.colLevels}</dt>
        <dd className="font-semibold tabular-nums" data-testid={`${testId}-levels`} data-pro={levels.pro} data-high={levels.high} data-medium={levels.medium} data-low={levels.low}>
          {levels.pro} · {levels.high} · {levels.medium} · {levels.low}
        </dd>
        {items.map(i => {
          const mine = rate(i.own[0], i.own[1]);
          const all = rate(i.field[0], i.field[1]);
          return (
            <div key={i.key} className="contents">
              <dt className="text-gray-500">{i.label}</dt>
              <dd className="tabular-nums" data-testid={`${testId}-${i.key}`} data-own={mine ?? ''} data-field={all ?? ''}>
                <span className="font-semibold">{mine}%</span> <span className="text-gray-400">({i.own[0]}/{i.own[1]})</span>
                {all !== null && <span className="text-gray-400"> · {i.fieldLabel.replace('{pct}', `${all}%`)}</span>}
              </dd>
            </div>
          );
        })}
        {/* #644 — the panel and the own-team cards of this tournament, when they have data. */}
        {q && (() => {
          const p = q.panel;
          const compared = panelCompared(p);
          const withPanel = panelWith(p);
          const own = ownTeamRounds(q.team);
          const off = ownTeamAgainstJury(q.team);
          return (
            <>
              {compared > 0 && (
                <>
                  <dt className="text-gray-500">{t.officials.colPanel}</dt>
                  <dd className="tabular-nums" data-testid={`${testId}-panel`} data-with={withPanel} data-compared={compared} data-upheld={p.dissent_upheld}>
                    <span className="font-semibold">{rate(withPanel, compared)}%</span> <span className="text-gray-400">({withPanel}/{compared})</span>
                    {p.dissent_upheld > 0 && <span className="text-gray-400"> · {t.officials.cellUpheld.replace('{n}', String(p.dissent_upheld))}</span>}
                  </dd>
                </>
              )}
              {own > 0 && (
                <>
                  <dt className="text-gray-500">{t.officials.colOwnTeam}</dt>
                  <dd className="tabular-nums" data-testid={`${testId}-own-team`} data-against-jury={off} data-for={q.team.for_own} data-against={q.team.against_own} data-rounds={own}>
                    <span className="font-semibold">{rate(off, own)}%</span> <span className="text-gray-400">({off}/{own})</span>
                    {off > 0 && <span className="text-gray-400"> · {ownSplit(t, q.team)}</span>}
                  </dd>
                </>
              )}
            </>
          );
        })()}
        {q && counts.ring_referee > 0 && (
          <>
            <dt className="text-gray-500">{t.officials.stoppagesShort}</dt>
            <dd className="font-semibold tabular-nums" data-testid={`${testId}-stoppages`}>{q.stoppages.stoppages}</dd>
          </>
        )}
      </dl>
    </li>
  );
}

/** The agreement of the official and of all judges at each tournament, oldest
 *  at the left. An SVG line chart (the app has no chart library): the
 *  print-stable pattern of BracketTree (viewBox, preserveAspectRatio="none",
 *  vectorEffect="non-scaling-stroke"). Needs 2 tournaments with cards. */
function AgreementChart({ timeline }: { timeline: OfficialTimelineRow[] }) {
  const { t } = useI18n();
  const points = timeline
    .filter(r => r.quality && r.quality.agreement.compared > 0)
    .map(r => ({ date: r.date, own: rate(r.quality!.agreement.matched, r.quality!.agreement.compared)!, field: rate(r.quality!.field.agreement.matched, r.quality!.field.agreement.compared)! }))
    .reverse();
  if (points.length < 2) return null;
  const x = (i: number) => (i / (points.length - 1)) * 100;
  // Height 40, with 2 units of room at the top and the bottom.
  const y = (p: number) => 2 + (1 - p / 100) * 36;
  const line = (key: 'own' | 'field') => points.map((p, i) => `${x(i)},${y(p[key])}`).join(' ');
  const first = points[0];
  const last = points[points.length - 1];
  // A screen reader gets the first and the last values, not only the title.
  const label = t.officials.chartAria
    .replace('{title}', t.officials.chartTitle)
    .replace('{from}', `${first.own}%`).replace('{to}', `${last.own}%`)
    .replace('{fieldFrom}', `${first.field}%`).replace('{fieldTo}', `${last.field}%`);
  return (
    <div data-testid="official-timeline-chart" data-points={points.length}>
      <div className="text-xs text-gray-500 mb-1">{t.officials.chartTitle}</div>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-xs text-gray-500 tabular-nums py-0.5"><span>100%</span><span>50%</span><span>0%</span></div>
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="flex-1 h-28 rounded bg-gray-50" role="img" aria-label={label}>
          <line x1="0" y1="20" x2="100" y2="20" className="stroke-gray-200" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          <polyline points={line('field')} fill="none" className="stroke-gray-400" strokeWidth={1.5} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
          <polyline points={line('own')} fill="none" className="stroke-indigo-600" strokeWidth={2} vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 mt-1 pl-10">
        <span>{formatDay(first.date, 'MMM yyyy')}</span>
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-indigo-600" />{t.officials.chartOfficial}</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-4 border-t-2 border-dashed border-gray-400" />{t.officials.chartField}</span>
        </span>
        <span>{formatDay(last.date, 'MMM yyyy')}</span>
      </div>
    </div>
  );
}

/** #644 — the own-team cards of the official against the jury decision at
 *  each tournament, oldest at the left. A card for the own corner and a card
 *  against it both count: a bias in one direction does not cancel a bias in
 *  the other (operator, 2026-10-22). Each point is the count of its own
 *  tournament, from 0 up; a tournament with no such card is 0. The legend
 *  gives the totals of all the tournaments. The same SVG pattern as
 *  AgreementChart. Each tournament has a hover title, and the table holds the
 *  same numbers. Only for an official with own-team rounds. */
function OwnTeamChart({ timeline }: { timeline: OfficialTimelineRow[] }) {
  const { t } = useI18n();
  const rows = timeline.filter(r => r.quality).slice().reverse();
  const own = (r: OfficialTimelineRow) => ownTeamRounds(r.quality!.team);
  if (rows.length < 2 || !rows.some(r => own(r) > 0)) return null;
  const points = rows.map(r => ({
    id: r.tournament_id, date: r.date, name: r.tournament_name,
    f: r.quality!.team.for_own, a: r.quality!.team.against_own, rounds: own(r),
    off: ownTeamAgainstJury(r.quality!.team),
  }));
  const sum = (k: 'f' | 'a' | 'off' | 'rounds') => points.reduce((n, p) => n + p[k], 0);
  const top = Math.max(1, ...points.map(p => p.off));
  const step = 100 / (points.length - 1);
  const x = (i: number) => i * step;
  // Height 40, with 2 units of room at the top and the bottom: 0 at the bottom.
  const y = (v: number) => 2 + (1 - v / top) * 36;
  const line = points.map((p, i) => `${x(i)},${y(p.off)}`).join(' ');
  const fill = (text: string, v: { n: number; m: number; f: number; a: number }) => text
    .replace('{n}', String(v.n)).replace('{m}', String(v.m)).replace('{for}', String(v.f)).replace('{against}', String(v.a));
  const totals = { n: sum('off'), m: sum('rounds'), f: sum('f'), a: sum('a') };
  const label = fill(t.officials.ownTeamChartAria.replace('{title}', t.officials.ownTeamChartTitle), totals);
  return (
    <div data-testid="official-own-team-chart" data-points={points.length} data-against-jury={totals.n} data-rounds={totals.m}
      data-for={totals.f} data-against={totals.a} data-values={points.map(p => p.off).join(',')}>
      <div className="text-xs text-gray-500">{t.officials.ownTeamChartTitle}</div>
      <div className="text-xs text-gray-400 mb-1">{t.officials.ownTeamChartHint}</div>
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-xs text-gray-500 tabular-nums py-0.5 w-8 text-right">
          {/* With no card against the jury, the top shows no number: the line lies on 0. */}
          <span>{points.some(p => p.off > 0) ? top : ''}</span><span>0</span>
        </div>
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="flex-1 h-28 rounded bg-gray-50" role="img" aria-label={label}>
          {/* 0: no card against the jury. */}
          <line x1="0" y1="38" x2="100" y2="38" className="stroke-gray-300" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          <polyline points={line} fill="none" className="stroke-indigo-600" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          {/* The hover target of each tournament: the band from half a step
              before the point to half a step after it, inside the chart. */}
          {points.map((p, i) => {
            const from = Math.max(0, x(i) - step / 2);
            const to = Math.min(100, x(i) + step / 2);
            return (
              <rect key={p.id} x={from} y={0} width={to - from} height={40} fill="transparent">
                {/* The numbers first, the name last: a name holds free text. */}
                <title>{fill(t.officials.ownTeamPoint, { n: p.off, m: p.rounds, f: p.f, a: p.a })
                  .replace('{date}', formatDay(p.date, 'dd MMM yyyy')).replace('{name}', p.name)}</title>
              </rect>
            );
          })}
        </svg>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 mt-1 pl-10">
        <span>{formatDay(points[0].date, 'MMM yyyy')}</span>
        <span className="inline-flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-indigo-600" />{t.officials.ownTeamChartLine}</span>
          <span>{fill(t.officials.ownTeamChartTotals, totals)}</span>
        </span>
        <span>{formatDay(points[points.length - 1].date, 'MMM yyyy')}</span>
      </div>
    </div>
  );
}
